/**
 * Checklist question type `table` (e.g. a Tagelohnzettel): fixed, typed columns and any number of
 * rows. Shared by template validation (server), answer normalization / snapshot and the PDF.
 * The field app mirrors the cell rules in `lib/checklistTable.ts`.
 */

export const CHECKLIST_TABLE_COLUMN_TYPES = ['text', 'number', 'date', 'choice', 'employee'] as const;
export type ChecklistTableColumnType = (typeof CHECKLIST_TABLE_COLUMN_TYPES)[number];

export const CHECKLIST_TABLE_MAX_COLUMNS = 20;
export const CHECKLIST_TABLE_MAX_ROWS = 50;
export const CHECKLIST_TABLE_CELL_MAX_LENGTH = 500;

export type ChecklistTableColumn = {
  /** Stable id: renaming a column must not break answers queued offline. */
  key: string;
  label: string;
  type: ChecklistTableColumnType;
  /** Number columns only, e.g. "Std.". */
  unit?: string;
  /** Choice columns only. */
  options?: string[];
  required?: boolean;
};

/** Employee cells keep the name so the protocol stays readable if the employee is deleted later. */
export type ChecklistTableEmployeeCell = { id: string | null; name: string };
export type ChecklistTableCell = string | number | ChecklistTableEmployeeCell;
export type ChecklistTableRow = Record<string, ChecklistTableCell>;

export type ChecklistTableConfig = { columns: ChecklistTableColumn[]; max_rows?: number };

function isColumnType(value: unknown): value is ChecklistTableColumnType {
  return (CHECKLIST_TABLE_COLUMN_TYPES as readonly unknown[]).includes(value);
}

function randomColumnKey(): string {
  return `c_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Cleans a table config from the builder / AI: whitelists column types, trims labels and options,
 * keeps keys unique (generates missing ones) and caps column / row counts.
 */
export function normalizeChecklistTableConfig(raw: unknown): ChecklistTableConfig {
  const cfg = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const used = new Set<string>();
  const columns: ChecklistTableColumn[] = [];
  for (const entry of Array.isArray(cfg.columns) ? cfg.columns : []) {
    if (!entry || typeof entry !== 'object') continue;
    const col = entry as Record<string, unknown>;
    const type = isColumnType(col.type) ? col.type : 'text';
    let key = String(col.key ?? '').trim().slice(0, 40);
    if (!key || used.has(key)) {
      do key = randomColumnKey();
      while (used.has(key));
    }
    used.add(key);
    const out: ChecklistTableColumn = { key, label: String(col.label ?? '').trim().slice(0, 80), type };
    if (type === 'number') {
      const unit = String(col.unit ?? '').trim().slice(0, 20);
      if (unit) out.unit = unit;
    }
    if (type === 'choice') {
      out.options = (Array.isArray(col.options) ? col.options : [])
        .map((o) => String(o ?? '').trim())
        .filter(Boolean)
        .slice(0, 50);
    }
    if (col.required === true) out.required = true;
    columns.push(out);
    if (columns.length >= CHECKLIST_TABLE_MAX_COLUMNS) break;
  }
  const result: ChecklistTableConfig = { columns };
  const maxRows = Number(cfg.max_rows);
  if (Number.isFinite(maxRows) && maxRows >= 1) {
    result.max_rows = Math.min(Math.floor(maxRows), CHECKLIST_TABLE_MAX_ROWS);
  }
  return result;
}

/** Builder validation message for a table question, or null when valid. */
export function checklistTableConfigError(config: ChecklistTableConfig): string | null {
  if (config.columns.length === 0) return 'Mindestens eine Spalte anlegen.';
  for (const [i, col] of config.columns.entries()) {
    if (!col.label) return `Spalte ${i + 1}: Bitte eine Überschrift eingeben.`;
    if (col.type === 'choice' && (col.options?.length ?? 0) < 2) {
      return `Spalte „${col.label}“: Mindestens 2 Optionen erforderlich.`;
    }
  }
  return null;
}

export function checklistTableMaxRows(config: ChecklistTableConfig): number {
  return config.max_rows ?? CHECKLIST_TABLE_MAX_ROWS;
}

function parseIsoDateCell(raw: string): string | null {
  let iso = raw;
  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(raw);
  if (de) iso = `${de[3]}-${de[2]!.padStart(2, '0')}-${de[1]!.padStart(2, '0')}`;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m! - 1 || dt.getUTCDate() !== d) return null;
  return iso;
}

type CellResult = { ok: true; cell: ChecklistTableCell | null } | { ok: false; error: string };

function normalizeCell(column: ChecklistTableColumn, raw: unknown): CellResult {
  if (column.type === 'employee') {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const obj = raw as Record<string, unknown>;
      const name = String(obj.name ?? '').trim().slice(0, CHECKLIST_TABLE_CELL_MAX_LENGTH);
      const id = String(obj.id ?? '').trim() || null;
      return { ok: true, cell: name ? { id, name } : null };
    }
    const name = String(raw ?? '').trim().slice(0, CHECKLIST_TABLE_CELL_MAX_LENGTH);
    return { ok: true, cell: name ? { id: null, name } : null };
  }
  if (column.type === 'number') {
    if (typeof raw === 'number') return Number.isFinite(raw) ? { ok: true, cell: raw } : { ok: false, error: 'Zahl' };
    const text = String(raw ?? '').trim();
    if (!text) return { ok: true, cell: null };
    const n = Number(text.replace(',', '.'));
    return Number.isFinite(n) ? { ok: true, cell: n } : { ok: false, error: 'Zahl' };
  }
  const text = String(raw ?? '').trim();
  if (!text) return { ok: true, cell: null };
  if (text.length > CHECKLIST_TABLE_CELL_MAX_LENGTH) return { ok: false, error: 'Länge' };
  if (column.type === 'date') {
    const iso = parseIsoDateCell(text);
    return iso ? { ok: true, cell: iso } : { ok: false, error: 'Datum' };
  }
  if (column.type === 'choice' && column.options?.length && !column.options.includes(text)) {
    return { ok: false, error: 'Auswahl' };
  }
  return { ok: true, cell: text };
}

const CELL_ERROR_TEXT: Record<string, string> = {
  Zahl: 'ist keine gültige Zahl',
  Datum: 'ist kein gültiges Datum',
  Auswahl: 'ist keine gültige Auswahl',
  Länge: `ist länger als ${CHECKLIST_TABLE_CELL_MAX_LENGTH} Zeichen`,
};

/**
 * Validates submitted rows against the columns: unknown keys are dropped, empty rows removed,
 * numbers parsed (comma allowed), dates normalized to ISO, choices checked, required cells enforced.
 * Returns an empty list when nothing was filled in.
 */
export function normalizeChecklistTableRows(
  config: ChecklistTableConfig,
  value: unknown,
  questionText: string,
): { ok: true; rows: ChecklistTableRow[] } | { ok: false; error: string } {
  const rawRows = Array.isArray(value) ? value : [];
  const rows: ChecklistTableRow[] = [];
  for (const rawRow of rawRows) {
    if (!rawRow || typeof rawRow !== 'object' || Array.isArray(rawRow)) continue;
    const source = rawRow as Record<string, unknown>;
    const row: ChecklistTableRow = {};
    for (const column of config.columns) {
      const result = normalizeCell(column, source[column.key]);
      if (result.ok === false) {
        return {
          ok: false,
          error: `„${questionText}“, Zeile ${rows.length + 1}: „${column.label}“ ${CELL_ERROR_TEXT[result.error] ?? 'ist ungültig'}.`,
        };
      }
      if (result.cell != null) row[column.key] = result.cell;
    }
    if (Object.keys(row).length === 0) continue;
    rows.push(row);
  }
  const maxRows = checklistTableMaxRows(config);
  if (rows.length > maxRows) {
    return { ok: false, error: `„${questionText}“: höchstens ${maxRows} Zeilen erlaubt.` };
  }
  for (const [i, row] of rows.entries()) {
    const missing = config.columns.find((c) => c.required && row[c.key] == null);
    if (missing) {
      return { ok: false, error: `„${questionText}“, Zeile ${i + 1}: „${missing.label}“ ist ein Pflichtfeld.` };
    }
  }
  return { ok: true, rows };
}

function formatNumberDe(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: Number.isInteger(n) ? 0 : 1, maximumFractionDigits: 2 });
}

/** Display text of one cell (PDF, web). Dates as DD.MM.YYYY, numbers with German decimals + unit. */
export function formatChecklistTableCell(column: ChecklistTableColumn, cell: ChecklistTableCell | undefined): string {
  if (cell == null) return '';
  if (typeof cell === 'object') return cell.name;
  if (typeof cell === 'number') return column.unit ? `${formatNumberDe(cell)} ${column.unit}` : formatNumberDe(cell);
  if (column.type === 'date') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(cell);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : cell;
  }
  return cell;
}

/** Sum per number column (only columns with at least one value), formatted for the PDF footer. */
export function checklistTableTotals(columns: ChecklistTableColumn[], rows: ChecklistTableRow[]): Record<string, string> {
  const totals: Record<string, string> = {};
  for (const column of columns) {
    if (column.type !== 'number') continue;
    const values = rows.map((r) => r[column.key]).filter((v): v is number => typeof v === 'number');
    if (values.length === 0) continue;
    totals[column.key] = formatChecklistTableCell(column, values.reduce((a, b) => a + b, 0));
  }
  return totals;
}
